import React, { useState, useMemo, useEffect, useRef, useDeferredValue } from 'react';
import { Modal, Form, Table, InputGroup, Button, Spinner } from 'react-bootstrap';

const SellerSearchModal = ({ show, onHide, onSelectSeller, sellers = [], clients = [], presupuestos = [], isLoading = false, user }) => {
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const searchInputRef = useRef(null);

    const loggedSeller = useMemo(() => {
        if (!user?.c_vende || !sellers || sellers.length === 0) return null;
        return sellers.find(s => String(s.c_vende).trim() === String(user.c_vende).trim());
    }, [sellers, user]);

    const isVendorOnly = useMemo(() => {
        if (!user?.c_vende) return false;
        if (loggedSeller) {
            return Boolean(loggedSeller.l_filven);
        }
        return true;
    }, [user, loggedSeller]);

    useEffect(() => {
        if (show) {
            setSearch('');
            setTimeout(() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 150);
        }
    }, [show]);

    // Consolidar lista de vendedores de la tabla de vendedores y de códigos encontrados en clientes/presupuestos
    const consolidatedSellers = useMemo(() => {
        const sellerMap = new Map();

        // 1. Agregar vendedores de la base de datos de vendedores
        if (Array.isArray(sellers) && sellers.length > 0) {
            sellers.forEach(s => {
                if (s && s.c_vende !== undefined && s.c_vende !== null) {
                    const code = String(s.c_vende).trim();
                    if (code && parseInt(s.c_situa, 10) !== 9) {
                        const name = `${s.d_apell || ''} ${s.d_nombr || ''}`.trim() || `Vendedor ${code}`;
                        sellerMap.set(code, {
                            c_vende: code,
                            d_apell: s.d_apell || '',
                            d_nombr: s.d_nombr || '',
                            name: name,
                            d_email: s.d_email || '',
                            n_telef: s.n_telef || s.n_celul || '',
                            d_domic: s.d_domic || ''
                        });
                    }
                }
            });
        }

        // Si el usuario es perfil vendedor estricto, solo puede verse a sí mismo
        if (isVendorOnly && user?.c_vende) {
            const userCode = String(user.c_vende).trim();
            const selfSeller = sellerMap.get(userCode) || {
                c_vende: userCode,
                d_apell: user.apellido || '',
                d_nombr: user.nombre || '',
                name: `${user.apellido || ''} ${user.nombre || ''}`.trim() || `Vendedor ${userCode}`,
                d_email: '',
                n_telef: '',
                d_domic: ''
            };
            return [selfSeller];
        }

        // 2. Agregar códigos de vendedores presentes en clientes si no estaban registrados
        if (Array.isArray(clients)) {
            clients.forEach(c => {
                if (c && c.c_vende !== undefined && c.c_vende !== null) {
                    const code = String(c.c_vende).trim();
                    if (code && !sellerMap.has(code)) {
                        sellerMap.set(code, {
                            c_vende: code,
                            d_apell: '',
                            d_nombr: '',
                            name: `Vendedor Código ${code}`,
                            d_email: '',
                            n_telef: '',
                            d_domic: ''
                        });
                    }
                }
            });
        }

        // 3. Agregar códigos de presupuestos si hubiese alguno
        if (Array.isArray(presupuestos)) {
            presupuestos.forEach(p => {
                if (p && p.c_vende !== undefined && p.c_vende !== null) {
                    const code = String(p.c_vende).trim();
                    if (code && !sellerMap.has(code)) {
                        sellerMap.set(code, {
                            c_vende: code,
                            d_apell: '',
                            d_nombr: '',
                            name: `Vendedor Código ${code}`,
                            d_email: '',
                            n_telef: '',
                            d_domic: ''
                        });
                    }
                }
            });
        }

        return Array.from(sellerMap.values()).sort((a, b) => {
            const numA = parseFloat(a.c_vende) || 0;
            const numB = parseFloat(b.c_vende) || 0;
            return numA - numB;
        });
    }, [sellers, clients, presupuestos, isVendorOnly, user]);

    // Filtrar vendedores según la búsqueda del usuario
    const filteredSellers = useMemo(() => {
        if (!deferredSearch || !deferredSearch.trim()) {
            return consolidatedSellers;
        }

        const term = deferredSearch.toLowerCase().trim();
        return consolidatedSellers.filter(s => 
            String(s.c_vende).toLowerCase().includes(term) ||
            s.name.toLowerCase().includes(term) ||
            (s.d_apell && s.d_apell.toLowerCase().includes(term)) ||
            (s.d_nombr && s.d_nombr.toLowerCase().includes(term)) ||
            (s.d_email && s.d_email.toLowerCase().includes(term)) ||
            (s.n_telef && s.n_telef.toLowerCase().includes(term))
        ).sort((a, b) => {
            const aExact = String(a.c_vende).toLowerCase() === term;
            const bExact = String(b.c_vende).toLowerCase() === term;
            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;

            const aStarts = String(a.c_vende).startsWith(term) || a.name.toLowerCase().startsWith(term);
            const bStarts = String(b.c_vende).startsWith(term) || b.name.toLowerCase().startsWith(term);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;

            return (parseFloat(a.c_vende) || 0) - (parseFloat(b.c_vende) || 0);
        });
    }, [consolidatedSellers, deferredSearch]);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="bg-primary text-white py-2">
                <Modal.Title className="fs-5">
                    <i className="bi bi-person-badge me-2"></i>Buscar Vendedor
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0 d-flex flex-column" style={{ maxHeight: '70vh' }}>
                {/* BUSCADOR SUPERIOR */}
                <div className="p-3 bg-light border-bottom">
                    <InputGroup>
                        <InputGroup.Text className="bg-white border-0 shadow-sm">
                            <i className="bi bi-search text-muted"></i>
                        </InputGroup.Text>
                        <Form.Control
                            ref={searchInputRef}
                            placeholder="Buscar por código, apellido, nombre o email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="border-0 shadow-sm"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && filteredSellers.length > 0) {
                                    onSelectSeller(filteredSellers[0]);
                                    onHide();
                                }
                            }}
                        />
                        {search && (
                            <Button 
                                variant="outline-secondary" 
                                className="border-0 shadow-sm"
                                onClick={() => setSearch('')}
                            >
                                <i className="bi bi-x-lg"></i>
                            </Button>
                        )}
                    </InputGroup>
                    <div className="text-muted small mt-2 px-1">
                        Mostrando <strong>{filteredSellers.length}</strong> vendedores disponibles
                    </div>
                </div>

                {/* TABLA DE VENDEDORES */}
                <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: '280px' }}>
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="text-muted mt-2">Cargando vendedores...</p>
                        </div>
                    ) : filteredSellers.length > 0 ? (
                        <Table hover size="sm" className="mb-0 align-middle">
                            <thead className="table-dark sticky-top" style={{ top: 0, zIndex: 10 }}>
                                <tr>
                                    <th style={{ width: '90px' }} className="ps-3">Código</th>
                                    <th>Apellido y Nombre</th>
                                    <th>Email / Contacto</th>
                                    <th>Teléfono</th>
                                    <th style={{ width: '90px' }} className="text-center pe-3">Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSellers.map((s) => (
                                    <tr 
                                        key={s.c_vende}
                                        onDoubleClick={() => {
                                            onSelectSeller(s);
                                            onHide();
                                        }}
                                        onTouchEnd={(e) => {
                                            const now = Date.now();
                                            const lastTap = e.currentTarget._lastTap || 0;
                                            if (now - lastTap < 300) {
                                                onSelectSeller(s);
                                                onHide();
                                            }
                                            e.currentTarget._lastTap = now;
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="fw-bold ps-3">{s.c_vende}</td>
                                        <td className="fw-semibold">{s.name}</td>
                                        <td className="text-muted small">{s.d_email || '-'}</td>
                                        <td className="text-muted small">{s.n_telef || '-'}</td>
                                        <td className="text-center pe-3">
                                            <Button 
                                                variant="success" 
                                                size="sm" 
                                                className="rounded-circle shadow-sm"
                                                style={{ width: '30px', height: '30px', padding: 0 }}
                                                onClick={() => {
                                                    onSelectSeller(s);
                                                    onHide();
                                                }}
                                                title="Seleccionar vendedor"
                                            >
                                                <i className="bi bi-check-lg"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center text-muted py-5">
                            <i className="bi bi-person-x text-secondary" style={{ fontSize: '3rem', opacity: 0.4 }}></i>
                            <h6 className="mt-2">No se encontraron vendedores</h6>
                            <p className="small">Intente buscar con otro término.</p>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="bg-light py-2">
                <Button variant="secondary" size="sm" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SellerSearchModal;
